import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Paper,
    Tabs,
    Tab,
    Chip,
    Stack,
    Avatar,
    LinearProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import {
    IconArrowLeft,
    IconUsers,
    IconMail,
    IconCheck,
    IconClock,
    IconCalendar,
    IconMapPin,
    IconPalette,
    IconSend,
    IconUpload,
    IconSettings,
    IconEye,
} from '@tabler/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Helmet } from 'react-helmet';
import * as XLSX from 'xlsx';
import MainCard from 'ui-component/cards/MainCard';
import AnimateButton from 'ui-component/extended/AnimateButton';
import ThemeUploadData from './ThemeUploadData';
import AdvancedDataTable from '../../Components/common/AdvancedDataTable';
import SendEmail from '../../Components/SendEmail';
import EmailTemplateSetup from './EmailTemplateSetup';
import SendInvitation from '../../Components/SendInvitation';
import ConfirmationEmailSetup from './ConfirmationEmailSetup';

// GraphQL query to get user permissions
const GET_ME = gql`
    query {
        me {
            permissions
        }
    }
`;

// GraphQL Queries
const GET_THEME = gql`
    query GetTheme($id: ID!, $eventId: String!) {
        theme(id: $id, eventId: $eventId) {
            id
            name
            description
            type
            workshopDates {
                id
                eventDate
                startTime
                endTime
                venue
                allowedRegistrations
                types {
                    type_label
                    allowed_registrations
                }
            }
            createdAt
            updatedAt
        }
    }
`;

const GET_THEME_ATTENDEES = gql`
    query GetThemeAttendees($themeId: String!, $eventId: String!, $role: ROLE!) {
        themeAttendees(themeId: $themeId, eventId: $eventId, role: $role) {
            id
            themeId
            workshopId
            workshopDate
            type
            eventDate
            isInvitationSend
            isConfirmed
            data
        }
    }
`;

const GET_FORM_FIELDS = gql`
    query GetFormFields($eventId: String!, $role: ROLE!) {
        form(eventId: $eventId, role: $role) {
            fields {
                name
                label
                type
                required
                options
            }
            features
        }
    }
`;

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role='tabpanel'
            hidden={value !== index}
            id={`theme-tabpanel-${index}`}
            aria-labelledby={`theme-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

// Insights Component
const Insights = ({ attendees, theme }) => {
    console.log('Insights debug - Raw attendees:', attendees);

    // Convert backend boolean values (1/0) to frontend boolean (true/false)
    const processedAttendees = attendees.map(attendee => ({
        ...attendee,
        isConfirmed: attendee.isConfirmed === 1 || attendee.isConfirmed === true,
        isInvitationSend: attendee.isInvitationSend === 1 || attendee.isInvitationSend === true,
    }));

    console.log('Insights debug - Processed attendees:', processedAttendees);

    const totalAttendees = processedAttendees.length;
    const invitedCount = processedAttendees.filter(a => a.isInvitationSend).length;
    const confirmedCount = processedAttendees.filter(a => a.isConfirmed).length;

    console.log('Insights counts:', { totalAttendees, invitedCount, confirmedCount });

    // Date-wise insights - match attendees by eventDate with workshop eventDate
    const dateWiseInsights = theme.workshopDates.map(workshop => {
        console.log(`Processing workshop ${workshop.id}:`, workshop);

        const workshopAttendees = processedAttendees.filter(a => {
            console.log('Checking attendee:', {
                attendeeId: a.id,
                attendeeEventDate: a.eventDate,
                attendeeWorkshopId: a.workshopId,
                workshopId: workshop.id,
                workshopEventDate: workshop.eventDate,
            });

            // First try to match by workshopId if available
            if (a.workshopId && workshop.id && a.workshopId === workshop.id) {
                console.log('Matched by workshopId');
                return true;
            }

            // Then try to match by date
            if (a.eventDate && workshop.eventDate) {
                try {
                    const attendeeDate = new Date(a.eventDate).toISOString().split('T')[0];
                    const workshopDate = new Date(workshop.eventDate).toISOString().split('T')[0];
                    const isMatch = attendeeDate === workshopDate;
                    console.log('Date comparison:', { attendeeDate, workshopDate, isMatch });
                    return isMatch;
                } catch (error) {
                    console.warn('Date parsing error:', error);
                }
            }

            return false;
        });

        console.log(`Workshop ${workshop.id} matched ${workshopAttendees.length} attendees`);
        console.log(
            'Workshop attendees details:',
            workshopAttendees.map(a => ({
                id: a.id,
                isConfirmed: a.isConfirmed,
                isInvitationSend: a.isInvitationSend,
                eventDate: a.eventDate,
                workshopId: a.workshopId,
            }))
        );

        const invitedCount = workshopAttendees.filter(a => a.isInvitationSend).length;
        const confirmedCount = workshopAttendees.filter(a => a.isConfirmed).length;

        console.log(`Workshop ${workshop.id} counts:`, {
            registered: workshopAttendees.length,
            invited: invitedCount,
            confirmed: confirmedCount,
        });

        return {
            date: workshop.eventDate,
            venue: workshop.venue,
            allowed: workshop.allowedRegistrations,
            registered: workshopAttendees.length,
            invited: invitedCount,
            confirmed: confirmedCount,
        };
    });

    return (
        <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 1 }}>
                        <IconUsers size={24} />
                    </Avatar>
                    <Typography variant='h4' color='primary.main' gutterBottom>
                        {totalAttendees}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Total Attendees
                    </Typography>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 1 }}>
                        <IconMail size={24} />
                    </Avatar>
                    <Typography variant='h4' color='info.main' gutterBottom>
                        {invitedCount}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Attendee Invited
                    </Typography>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1 }}>
                        <IconCheck size={24} />
                    </Avatar>
                    <Typography variant='h4' color='success.main' gutterBottom>
                        {confirmedCount}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Attendee Confirmed
                    </Typography>
                </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 1 }}>
                        <IconClock size={24} />
                    </Avatar>
                    <Typography variant='h4' color='warning.main' gutterBottom>
                        {totalAttendees - confirmedCount}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        Pending
                    </Typography>
                </Card>
            </Grid>

            {/* Workshop Date-wise Insights */}
            {dateWiseInsights.map((insight, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                    <Card sx={{ textAlign: 'center', p: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', mx: 'auto', mb: 1 }}>
                            <IconCalendar size={24} />
                        </Avatar>
                        <Typography variant='h5' color='secondary.main' gutterBottom>
                            {insight.registered} / {insight.allowed}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            {new Date(insight.date).toLocaleDateString('en-US', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                            })}{' '}
                            ({insight.venue})
                        </Typography>
                        <Typography variant='caption' color='text.disabled'>
                            Confirmed: {insight.confirmed} | Invited: {insight.invited}
                        </Typography>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

// Attendees Tab Component
const AttendeesTab = ({ attendees, formFields, formFeatures, theme, eventId, role, permissions = [] }) => {
    const [selectedIds, setSelectedIds] = useState([]);

    // Transform attendees data for AdvancedDataTable
    const transformedData = attendees.map(attendee => {
        // Format dates consistently
        const formatDate = dateValue => {
            if (!dateValue) return '';
            try {
                const date = new Date(dateValue);
                if (isNaN(date.getTime())) return '';
                return date.toISOString().split('T')[0]; // YYYY-MM-DD format
            } catch (error) {
                return '';
            }
        };

        // Convert backend boolean values (1/0) to frontend boolean (true/false)
        const isConfirmed = attendee.isConfirmed === 1 || attendee.isConfirmed === true;
        const isInvitationSend = attendee.isInvitationSend === 1 || attendee.isInvitationSend === true;

        return {
            ...attendee,
            // Flatten the data object for easier access
            ...attendee.data,
            // Add status field for filtering
            status: isConfirmed ? 'Confirmed' : isInvitationSend ? 'Invited' : 'Not Invited',
            // Add badge visibility logic - ONLY for confirmed attendees
            isBadgeShow: isConfirmed,
            // Add formatted dates for filtering
            eventDate: formatDate(attendee.eventDate),
            workshopDate: formatDate(attendee.workshopDate),
            // Keep boolean values for badge logic
            isConfirmed: isConfirmed, // Keep as boolean for badge eligibility
            isInvitationSend: isInvitationSend, // Keep as boolean
            // Add display fields for table
            isConfirmedDisplay: isConfirmed ? 'Yes' : 'No',
            isInvitationSendDisplay: isInvitationSend ? 'Yes' : 'No',
            // Add workshop information
            workshopId: attendee.workshopId || '',
        };
    });

    // Debug: Log the transformed data to see the structure
    console.log('ThemeDetail - Transformed Data Sample:', transformedData.slice(0, 2));
    console.log(
        'ThemeDetail - Badge Logic Check:',
        transformedData.map(row => ({
            id: row.id,
            isConfirmed: row.isConfirmed,
            isBadgeShow: row.isBadgeShow,
            status: row.status,
        }))
    );

    // Create columns configuration for AdvancedDataTable
    const columns = [
        // Form fields columns
        ...formFields.map(field => ({
            key: field.name,
            label: field.label || field.name,
            minWidth: 120,
        })),
        // Additional columns
        {
            key: 'workshopDate',
            label: 'Workshop Date',
            minWidth: 120,
        },
        {
            key: 'type',
            label: 'Type',
            minWidth: 100,
        },
        {
            key: 'isConfirmedDisplay',
            label: 'Confirmed',
            minWidth: 100,
        },
        {
            key: 'isInvitationSendDisplay',
            label: 'Invitation Sent',
            minWidth: 120,
        },
        {
            key: 'eventDate',
            label: 'Event Date',
            minWidth: 120,
        },
        {
            key: 'workshopId',
            label: 'Workshop ID',
            minWidth: 100,
        },
    ];

    // Define which columns should have filters
    const filterColumns = [
        'type',
        'workshopDate',
        'isConfirmedDisplay',
        'isInvitationSendDisplay',
        'eventDate',
        'workshopId',
    ];

    // Define which columns should be searchable
    const searchableColumns = formFields
        .map(field => field.name)
        .concat(['type', 'workshopDate', 'isConfirmedDisplay', 'isInvitationSendDisplay', 'eventDate', 'workshopId']);

    // Define which columns should be sortable
    const sortableColumns = formFields
        .map(field => field.name)
        .concat(['workshopDate', 'type', 'isConfirmedDisplay', 'isInvitationSendDisplay', 'eventDate', 'workshopId']);

    // Handle export functionality
    const handleExport = async (format, data) => {
        try {
            console.log('ThemeDetail Export started:', { format, dataLength: data.length });

            const exportData = data.map(row => {
                const exportRow = { ...row };
                // Remove internal fields that shouldn't be exported
                delete exportRow.index;
                delete exportRow.data;
                return exportRow;
            });

            const filename = `theme-attendees-${theme?.name || 'theme'}-${eventId}-${
                new Date().toISOString().split('T')[0]
            }`;
            console.log('ThemeDetail Export filename:', filename);

            if (format === 'excel') {
                console.log('Creating Excel file...');
                const ws = XLSX.utils.json_to_sheet(exportData);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Attendees');
                XLSX.writeFile(wb, `${filename}.xlsx`);
                console.log('Excel file created successfully');
            } else if (format === 'csv') {
                console.log('Creating CSV file...');
                const csv = XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(exportData));
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `${filename}.csv`;
                link.click();
                console.log('CSV file created successfully');
            } else if (format === 'json') {
                console.log('Creating JSON file...');
                const jsonStr = JSON.stringify(exportData, null, 2);
                const blob = new Blob([jsonStr], { type: 'application/json' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `${filename}.json`;
                link.click();
                console.log('JSON file created successfully');
            }

            setSnackbar({
                open: true,
                message: `Export successful! ${data.length} records exported.`,
                severity: 'success',
            });
        } catch (error) {
            console.error('Export error:', error);
            setSnackbar({
                open: true,
                message: 'Export failed: ' + error.message,
                severity: 'error',
            });
        }
    };

    // Handle selection change for bulk actions
    const handleSelectionChange = selectedRows => {
        console.log('Selected rows:', selectedRows);
        setSelectedIds(selectedRows.map(row => row.id));
    };

    // Handle refetch after operations
    const handleRefetch = () => {
        console.log('Refetching data...');
        // Implement refetch logic here
    };

    return (
        <Box>
            <AdvancedDataTable
                data={transformedData}
                columns={columns}
                title={`${theme?.name || 'Theme'} Attendees`}
                pageSize={25}
                maxHeight={600}
                isAdmin={permissions.includes('admin')}
                permissions={permissions}
                tableRole={role}
                eventId={eventId}
                features={formFeatures} // Add features for badge eligibility
                onExport={handleExport}
                showSearch={true}
                showFilters={true}
                showPagination={true}
                showChips={false}
                chipColumns={[]}
                filterColumns={filterColumns}
                sortableColumns={sortableColumns}
                searchableColumns={searchableColumns}
                onSelectionChange={handleSelectionChange}
                onRefetch={handleRefetch}
                hideActions={false}
            />

            {/* Action Buttons - Show when rows are selected */}
            {selectedIds.length > 0 && permissions.includes('admin') && permissions.includes('bulk-email') && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <SendInvitation
                        selectedIds={selectedIds}
                        eventId={eventId}
                        role={role}
                        themeId={theme?.id}
                        theme={theme}
                        onSuccess={() => {
                            // Refetch attendees data after sending invitations
                            if (handleRefetch) {
                                handleRefetch();
                            }
                        }}
                    />
                    <SendEmail selectedIds={selectedIds.map(id => ({ id }))} eventId={eventId} role={role} />
                </Box>
            )}
        </Box>
    );
};

// Main Component
const ThemeDetail = () => {
    const { eventId, role, themeId } = useParams();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const {
        data: themeData,
        loading: themeLoading,
        error: themeError,
    } = useQuery(GET_THEME, {
        variables: { id: themeId, eventId },
        errorPolicy: 'all',
    });

    const {
        data: attendeesData,
        loading: attendeesLoading,
        error: attendeesError,
        refetch: refetchAttendees,
    } = useQuery(GET_THEME_ATTENDEES, {
        variables: { themeId, eventId, role },
        errorPolicy: 'all',
    });

    const { data: formData } = useQuery(GET_FORM_FIELDS, {
        variables: { eventId, role },
        skip: !eventId || !role,
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
    });

    const { data: userData } = useQuery(GET_ME);

    const theme = themeData?.theme;
    const attendees = attendeesData?.themeAttendees || [];
    const formFields = formData?.form?.fields || [];
    const formFeatures = formData?.form?.features || [];
    const permissions = userData?.me?.permissions?.split(',') || [];

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    if (themeLoading || attendeesLoading) {
        return <LinearProgress />;
    }

    if (themeError || attendeesError) {
        return (
            <Alert severity='error' sx={{ mt: 2 }}>
                Error loading theme: {themeError?.message || attendeesError?.message}
            </Alert>
        );
    }

    if (!theme) {
        return (
            <Alert severity='error' sx={{ mt: 2 }}>
                Theme not found
            </Alert>
        );
    }

    return (
        <>
            <Helmet>
                <title>{theme.name} - Theme Details</title>
            </Helmet>

            <MainCard>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Button startIcon={<IconArrowLeft size={20} />} onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                        Back
                    </Button>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant='h4' gutterBottom>
                            {theme.name}
                        </Typography>
                        <Typography variant='body1' color='text.secondary'>
                            {theme.description}
                        </Typography>
                        {theme.type && (
                            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                                Type: {theme.type}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label='Attendees' icon={<IconUsers size={20} />} iconPosition='start' />
                        <Tab label='Upload Data' icon={<IconUpload size={20} />} iconPosition='start' />
                        <Tab label='Setup Invitation Email' icon={<IconMail size={20} />} iconPosition='start' />
                        <Tab label='Setup Confirmation Email' icon={<IconSettings size={20} />} iconPosition='start' />
                    </Tabs>
                </Box>

                {/* Tab Content */}
                <TabPanel value={tabValue} index={0}>
                    <Insights attendees={attendees} theme={theme} />
                    <AttendeesTab
                        attendees={attendees}
                        formFields={formFields}
                        formFeatures={formFeatures}
                        theme={theme}
                        eventId={eventId}
                        role={role}
                        permissions={permissions}
                    />
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <ThemeUploadData
                        themeId={themeId}
                        themeName={theme.name}
                        theme={theme}
                        onUploadSuccess={() => {
                            // Refetch attendees data when upload is successful
                            if (refetchAttendees) {
                                refetchAttendees();
                            }
                        }}
                    />
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <EmailTemplateSetup
                        contextId={eventId}
                        templateType='invitation'
                        title='Setup Invitation Email Template'
                        eventId={eventId}
                        role={role}
                        themeId={themeId}
                        onSave={() => {
                            setSnackbar({
                                open: true,
                                message: 'Invitation email template saved successfully',
                                severity: 'success',
                            });
                        }}
                    />
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                    <ConfirmationEmailSetup
                        contextId={eventId}
                        themeId={themeId}
                        theme={theme}
                        title='Setup Confirmation Email Templates'
                        eventId={eventId}
                        role={role}
                        onSave={() => {
                            setSnackbar({
                                open: true,
                                message: 'Confirmation email templates saved successfully',
                                severity: 'success',
                            });
                        }}
                    />
                </TabPanel>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </MainCard>
        </>
    );
};

export default ThemeDetail;
